/**
 * Webtero — Main JavaScript Entry Point
 */

// Import CSS (Vite extracts to dist/css/theme.css in production)
import '../css/style.css';
import '../sass/style.scss';

// Import Splide
import Splide from '@splidejs/splide';

// Import components
import { mobileMenu } from './components/mobile-menu.js';
import { smoothScroll } from './components/smooth-scroll.js';
import { headerShadow } from './components/header-shadow.js';
import { revealOnScroll } from './components/reveal-on-scroll.js';
import { mainFeaturesScroll } from './components/main-features-scroll.js';
import { heroSlider } from './components/hero-slider.js';
import { megaMenu } from './components/mega-menu.js';
import { faqAccordion } from './components/faq-accordion.js';
import { imageSlider, ensureLightbox } from './components/image-slider.js';
import { pricing } from './components/pricing.js';
import { youtubeFacade } from './components/youtube-facade.js';
import { jotformEmbed, jotformEager } from './components/jotform-embed.js';
import { helpSearch } from './components/help-search.js';
import { locationAlert } from './components/location-alert.js';
import { cookieConsent } from './components/cookie-consent.js';
import { projectsLoadMore } from './components/projects-load-more.js';
import { testimonialsSlider } from './components/testimonials-slider.js';
import { reviewFull } from './components/review-full.js';
import { capacityMeter } from './components/capacity-meter.js';
import { platformFlags } from './components/platform.js';
import { previewFrames } from './components/preview-frames.js';
import { webinarCountdown } from './components/webinar-countdown.js';
import { webinarCapture } from './components/webinar-capture.js';
import { webinarEmbed } from './components/webinar-embed.js';
import { clientMessages } from './components/client-messages.js';
import { phoneConversations } from './components/phone-conversations.js';
import { familyMenu } from './components/family-menu.js';
import { storyModal } from './components/story-modal.js';
import { comicSteps } from './components/comic-steps.js';
import { countUp } from './components/count-up.js';
import { heroVideo } from './components/hero-video.js';
import { roundVideo } from './components/round-video.js';

// Helpers
export const helpers = {
  isTouch: () => {
    return (
      typeof window !== 'undefined' &&
      ('ontouchstart' in window || navigator.maxTouchPoints > 0)
    );
  },

  debounce: (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  throttle: (func, limit) => {
    let inThrottle;
    return function (...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  },

  scrollTo: (element, offset = 0) => {
    const target =
      typeof element === 'string' ? document.querySelector(element) : element;

    if (target) {
      const targetPosition =
        target.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth',
      });
    }
  },
};

// App state
export const state = {
  isMobileMenuOpen: false,
};

// Critical — interactive nav/header + anything that produces an above-the-fold visual
// (scroll-reveal initial state, the location prompt, the cookie banner). These are
// lightweight and must run promptly: revealing them late counts as a late visual change
// and inflates Speed Index. revealOnScroll/locationAlert/cookieConsent only touch the DOM
// (no network now that geo is timezone-based), so they're cheap here.
function initCritical() {
  familyMenu();
  platformFlags(); // flag <html> (.is-ios) before the cookie banner shows → gate WebKit backdrop-filter quirks
  mobileMenu(state);
  headerShadow(helpers);
  smoothScroll(helpers);
  megaMenu();
  revealOnScroll();
  locationAlert();
  cookieConsent();
  clientMessages(); // collapse extra conversations before a visitor reaches the section
  capacityMeter(); // live scarcity sheet (Prague weeks; fills each Wed 18:00) — DOM-only, hydrate before scroll
  previewFrames(); // preview-links tiles: scale the 1440px iframes to tile width before they paint
  jotformEager(); // microsites only: the iframe is already in the HTML — attach resize/loader now, not after load+idle
  storyModal(); // funnel v2: opening the "Příběh zakázky" dialog is a primary click, so its
                // listener must exist from the start rather than after load + idle
  heroVideo(); // funnel v2: the hero clip starts muted right away — waiting for load + idle defeats it
  countUp(); // funnel v2 trust bar sits right under the hero — resetting it after load would flicker
  youtubeFacade(); // only binds clicks, and a play button can sit in a hero — on a microsite
                   // `load` also waits for the eager JotForm iframe, so deferring it left
                   // the first seconds of clicks dead
}

// Non-critical — sliders etc. Run after first paint so the Splide mounts / layout reads
// (forced reflow) stay off the critical render path.
function initDeferred() {
  mainFeaturesScroll();
  heroSlider(Splide);
  faqAccordion();
  imageSlider(Splide);
  phoneConversations(Splide);
  // Help-article image blocks use the same lightbox but have no [data-image-slider],
  // so imageSlider() returns early without registering it — wire it up when triggers exist.
  if (document.querySelector('[data-lightbox]')) ensureLightbox();
  pricing(Splide);
  jotformEmbed(); // main site: after load, so layout is settled → below-fold forms stay lazy until scrolled near
  helpSearch();
  projectsLoadMore();
  testimonialsSlider(Splide);
  reviewFull();
  webinarCountdown(); // funnel: public urgency countdown, fetches its target fresh (cache-safe)
  webinarCapture(); // funnel: email capture → double opt-in
  webinarEmbed(); // funnel: state-driven webinar block — countdown / server-gated video / register-again
  roundVideo(); // funnel v2: Telegram-style clip beside the outcome — far below the fold
  comicSteps(); // funnel v2: turn-over process coins — tap toggle + a one-time peek when scrolled to

  if (import.meta.env.DEV) {
    console.log('Webtero initialized');
  }
}

function whenIdle(fn) {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(fn, { timeout: 2000 });
  } else {
    setTimeout(fn, 0);
  }
}

function start() {
  initCritical();
  if (document.readyState === 'complete') {
    whenIdle(initDeferred);
  } else {
    window.addEventListener('load', () => whenIdle(initDeferred), { once: true });
  }
}

// DOM Ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
